import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  Tab,
  Tabs,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Fade,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close,
  CreditCard,
  QrCode,
  CheckCircle,
  Scale,
  Gavel,
  Business,
  TrendingUp,
  AccountBalance,
  Diamond,
} from '@mui/icons-material';
import { useCredit } from '../contexts/CreditContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ProfessionalSubscriptionPopupProps {
  open: boolean;
  onClose?: () => void;
  isBlocked?: boolean;
}

const ProfessionalSubscriptionPopup: React.FC<ProfessionalSubscriptionPopupProps> = ({
  open,
  onClose,
  isBlocked = false,
}) => {
  // Proteção contra dupla renderização
  if (!open) {
    return null;
  }
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { credits } = useCredit();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(0); // 0 = Planos, 1 = Avulso
  const [selectedPlan, setSelectedPlan] = useState<string>('professional');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isAnnual, setIsAnnual] = useState(true); // Toggle para planos anuais - Padrão: Anual



  // Planos com features realistas e escala rentável de créditos
  const plans = [
    {
      id: 'basic',
      name: 'Smart',
      subtitle: 'Perfeito para começar',
      price: 49.90,
      priceAnnual: 449.90,
      credits: '60',
      savings: 'R$ 0,83/crédito',
      icon: <Gavel />,
      popular: false,
      features: [
        'Até 30 gerações de petições',
        'Até 120 mensagens no chat IA',
        'Análise e revisão de documentos',
        'Templates jurídicos básicos',
        'Suporte por email',
        'Créditos renovados mensalmente'
      ]
    },
    {
      id: 'professional', 
      name: 'Smart Plus',
      subtitle: 'Melhor custo benefício',
      price: 99.90,
      priceAnnual: 899.90,
      credits: '150',
      savings: 'R$ 0,67/crédito (20% melhor)',
      icon: <Business />,
      popular: true,
      features: [
        'Até 75 gerações de petições',
        'Até 300 mensagens no chat IA',
        'Análise e revisão avançada',
        'Todos os templates jurídicos',
        'Busca de jurisprudência',
        'Histórico completo de conversas',
        'Suporte prioritário',
        'Créditos renovados mensalmente'
      ]
    },
    {
      id: 'expert',
      name: 'Team',
      subtitle: 'Para escritórios',
      price: 189.90,
      priceAnnual: 1709.90,
      credits: '350',
      savings: 'R$ 0,54/crédito (35% melhor)',
      icon: <TrendingUp />,
      popular: false,
      features: [
        'Até 175 gerações de petições',
        'Até 700 mensagens no chat IA',
        'Análise avançada com parecer jurídico',
        'Todos os templates premium',
        'Busca avançada de jurisprudência',
        'Compartilhamento em equipe',
        'Histórico ilimitado',
        'Suporte prioritário',
        'Créditos renovados mensalmente'
      ]
    },
    {
      id: 'unlimited',
      name: 'Enterprise',
      subtitle: 'Poder ilimitado',
      price: 299.90,
      priceAnnual: 2699.90,
      credits: '700',
      savings: 'R$ 0,43/crédito (48% melhor)',
      icon: <Diamond />,
      popular: false,
      features: [
        'Até 350 gerações de petições',
        'Até 1400 mensagens no chat IA',
        'Análise jurídica completa com IA',
        'Templates personalizados',
        'Busca ilimitada de jurisprudência',
        'Multiusuários (até 5)',
        'API de integração',
        'Consultoria para implementação',
        'Suporte dedicado 24/7',
        'Créditos renovados mensalmente'
      ]
    }
  ];

  const quickValues = [
    { value: 25, label: '30 créditos', credits: 30, popular: false },
    { value: 50, label: '65 créditos', credits: 65, popular: true, savings: '30% extra' },
    { value: 100, label: '140 créditos', credits: 140, popular: false, savings: '40% extra' },
    { value: 200, label: '300 créditos', credits: 300, popular: false, savings: '50% extra' },
  ];

  const handleSubscribe = () => {

    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return;
    
    const planPrice = isAnnual ? plan.priceAnnual : plan.price;
    const planName = isAnnual ? `${plan.name} (Anual)` : plan.name;
    

    navigate('/checkout', {
      state: {
        amount: planPrice,
        method: 'credit',
        fromPage: 'subscription',
        isPlanSubscription: true,
        planType: selectedPlan,
        isAnnual: isAnnual,
        planData: {
          id: plan.id,
          name: planName,
          description: plan.subtitle || '',
          amount: planPrice,
          features: plan.features,
          credits: plan.credits,
          savings: plan.savings
        }
      }
    });

    if (onClose) onClose();
  };

  const handleOneTimePayment = () => {
    if (!customAmount || parseFloat(customAmount) <= 0) return;
    
    navigate('/checkout', {
      state: {
        amount: parseFloat(customAmount),
        method: 'pix',
        fromPage: 'credits',
        isPlanSubscription: false,
      }
    });
    if (onClose) onClose();
  };

  const handleQuickPurchase = (amount: number) => {
    navigate('/checkout', {
      state: {
        amount: amount,
        method: 'pix',
        fromPage: 'credits',
        isPlanSubscription: false,
      }
    });

    if (onClose) onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      TransitionComponent={Fade}

      PaperProps={{
        sx: {
          width: isMobile ? '100vw' : 'calc(100vw - 80px)', // Quase toda a largura, só deixa 40px de cada lado
          maxWidth: isMobile ? '100vw' : '1400px', // Máximo para telas muito grandes
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : 'calc(100vh - 80px)', // Quase toda a altura também
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : 'auto',
          overflow: 'visible',
        }
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'visible' }}>
        {/* Header Integrado com Saldo */}
        <Box sx={{ 
          p: 2,
          position: 'relative',
          bgcolor: 'white',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          {/* Botão Fechar */}
          {!isBlocked && onClose && (
            <IconButton 
              onClick={onClose} 
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                color: theme.palette.text.secondary,
                '&:hover': { 
                  bgcolor: theme.palette.action.hover,
                  color: theme.palette.primary.main
                }
              }}
            >
              <Close />
            </IconButton>
          )}

          {/* Layout Flexível: Título à esquerda, Saldo à direita */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            pr: 5, // Espaço para o botão fechar
            mb: 0
          }}>
            {/* Lado Esquerdo: Título e Subtítulo */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 0 }}>
                {isBlocked 
                  ? 'Créditos Insuficientes' 
                  : credits <= 5 
                  ? 'Créditos Baixos' 
                  : 'Desbloqueie o Potencial Máximo da Advogada Parceira'
                }
              </Typography>
              
              {(isBlocked || credits <= 5) && (
                <Typography variant="subtitle1" color="text.secondary">
                  {isBlocked 
                    ? 'Recarregue para continuar usando a plataforma'
                    : 'Recomendamos recarregar agora para não interromper seu trabalho'
                  }
                </Typography>
              )}
            </Box>

            {/* Lado Direito: Saldo Atual - CORES CORRIGIDAS */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              minWidth: 120
            }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'white',  // Fundo sempre branco
                color: isBlocked ? theme.palette.error.main : 
                       credits <= 5 ? theme.palette.warning.main : 
                       theme.palette.primary.main,  // Texto colorido
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `2px solid ${isBlocked ? theme.palette.error.main : 
                                     credits <= 5 ? theme.palette.warning.main : 
                                     theme.palette.primary.main}`,
                boxShadow: 1,
              }}>
                <AccountBalance sx={{ fontSize: 20 }} />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, opacity: 0.8 }}>
                    Saldo Atual
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
                    {credits.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Tabs Integradas no Header */}
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            centered
            sx={{ 
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: 3,
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '1rem',
                color: theme.palette.text.secondary,
                minHeight: 56,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                }
              }
            }}
          >
            <Tab 
              label="Planos" 
              icon={<CreditCard />}
              iconPosition="start"
            />
            <Tab 
              label="Compra Avulsa" 
              icon={<QrCode />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Conteúdo Principal - Direto aos Planos */}
        <Box sx={{ bgcolor: theme.palette.background.default, overflow: 'visible' }}>
          {/* Tab 1: Planos */}
          {activeTab === 0 && (
            <Box sx={{ p: 2, pt: 2, overflow: 'visible' }}>
              <Typography variant="h6" sx={{ mb: 0.5, textAlign: 'center', fontWeight: 'bold', color: theme.palette.primary.main }}>
                Planos Mensais
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, textAlign: 'center', color: 'text.secondary' }}>
                Escolha o plano ideal para sua prática jurídica
              </Typography>
              
              {/* Toggle Mensal/Anual Melhorado */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: 1
                }}>
                  <Typography 
                    variant="body1" 
                    fontWeight="bold"
                    color={!isAnnual ? 'primary' : 'text.secondary'}
                    sx={{ 
                      cursor: 'pointer',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: !isAnnual ? theme.palette.primary.main : 'transparent',
                      color: !isAnnual ? 'white' : 'text.secondary',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => setIsAnnual(false)}
                  >
                    Mensal
                  </Typography>
                  
                  <Box
                    sx={{ 
                      position: 'relative',
                      display: 'inline-block'
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      fontWeight="bold"
                      color={isAnnual ? 'primary' : 'text.secondary'}
                      sx={{ 
                        cursor: 'pointer',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: isAnnual ? theme.palette.primary.main : 'transparent',
                        color: isAnnual ? 'white' : 'text.secondary',
                        transition: 'all 0.3s ease',
                      }}
                      onClick={() => setIsAnnual(true)}
                    >
                      Anual
                    </Typography>
                    <Chip 
                      label="25% OFF" 
                      color="success" 
                      size="small"
                      sx={{ 
                        position: 'absolute',
                        top: -12,
                        right: -8,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        bgcolor: '#4CAF50',
                        color: 'white',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              
              <Grid container spacing={2} justifyContent="center" sx={{ pt: 1, overflow: 'visible' }}>
                {plans.map((plan) => (
                  <Grid item xs={12} sm={6} md={3} key={plan.id} sx={{ overflow: 'visible' }}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedPlan === plan.id ? 2 : 1,
                        borderColor: selectedPlan === plan.id ? theme.palette.primary.main : 'grey.300',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        mt: 2.5,
                        overflow: 'visible',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.shadows[8],
                          borderColor: theme.palette.primary.main,
                        }
                      }}
                      onClick={() => setSelectedPlan(plan.id)}
                    >
                      {/* Badge Popular */}
                      {plan.popular && (
                        <Chip
                          label="MAIS ESCOLHIDO"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            right: 16,
                            bgcolor: theme.palette.secondary.main,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            zIndex: 1,
                            boxShadow: 2,
                          }}
                        />
                      )}
                      
                      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Header do Plano */}
                        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                          <Box sx={{ color: theme.palette.primary.main, mb: 0.5 }}>
                            {React.cloneElement(plan.icon, { fontSize: 'medium' })}
                          </Box>
                          
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {plan.name}
                          </Typography>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            {plan.subtitle}
                          </Typography>
                          
                          {isAnnual ? (
                            <>
                              {/* Plano Anual - Destaque no valor mensal */}
                              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 0.5 }}>
                                <Typography variant="h4" color={theme.palette.primary.main} fontWeight="bold">
                                  R$ {(plan.priceAnnual / 12).toFixed(2).replace('.', ',')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  /mês
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Total anual: R$ {plan.priceAnnual.toFixed(2).replace('.', ',')}
                              </Typography>
                            </>
                          ) : (
                            <>
                              {/* Plano Mensal */}
                              <Typography variant="h4" color={theme.palette.primary.main} fontWeight="bold" sx={{ mb: 0.5 }}>
                                R$ {plan.price.toFixed(2).replace('.', ',')}
                              </Typography>
                            </>
                          )}
                          
                          <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {plan.credits} créditos/mês
                          </Typography>
                          
                          {/* Chip de economia sempre na mesma posição */}
                          {isAnnual ? (
                            <Chip 
                              label="25% de desconto"
                              color="success"
                              size="small"
                              sx={{ mb: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          ) : plan.savings && (
                            <Chip 
                              label={plan.savings}
                              color="success"
                              size="small"
                              sx={{ mb: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>

                        {/* Recursos */}
                        <List dense sx={{ flex: 1, py: 0 }}>
                          {plan.features.map((feature, index) => (
                            <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary={feature}
                                primaryTypographyProps={{
                                  variant: 'caption',
                                  color: 'text.primary'
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                                          onClick={handleSubscribe}
                  disabled={!selectedPlan}
                  sx={{
                    minWidth: 250,
                    py: 2,
                    px: 4,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <CreditCard sx={{ mr: 1 }} />
                  ASSINAR PLANO
                </Button>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Pagamento recorrente seguro com cartão de crédito
                </Typography>
              </Box>
            </Box>
          )}

          {/* Tab 2: Compra Avulsa */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold', color: theme.palette.primary.main }}>
                Recarga rápida de créditos:
              </Typography>

              {/* Valores Rápidos */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {quickValues.map((option) => (
                  <Grid item xs={3} key={option.value}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: option.popular ? 2 : 1,
                        borderColor: option.popular ? theme.palette.secondary.main : 'grey.300',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          borderColor: theme.palette.primary.main,
                          boxShadow: theme.shadows[4],
                        }
                      }}
                                              onClick={() => handleQuickPurchase(option.value)}
                    >
                      {option.popular && (
                        <Chip
                          label="POPULAR"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: 8,
                            bgcolor: theme.palette.secondary.main,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            zIndex: 1,
                          }}
                        />
                      )}
                      
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          R$ {option.value}
                        </Typography>
                        <Typography variant="body1" color="text.primary" fontWeight="bold">
                          {option.credits} créditos
                        </Typography>
                        {option.savings && (
                          <Chip 
                            label={option.savings}
                            color="success"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          R$ {(option.value / option.credits).toFixed(2)} por crédito
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Valor Personalizado */}
              <TextField
                fullWidth
                label="Valor personalizado (R$)"
                type="number"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{ mb: 3 }}
              />

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleOneTimePayment}
                  disabled={!customAmount || parseFloat(customAmount) <= 0}
                  sx={{
                    minWidth: 250,
                    py: 2,
                    px: 4,
                    bgcolor: theme.palette.secondary.main,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: theme.palette.secondary.dark,
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <QrCode sx={{ mr: 1 }} />
                  COMPRAR R$ {customAmount || '0,00'}
                </Button>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  PIX instantâneo ou cartão de crédito
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProfessionalSubscriptionPopup; 